import React, { ChangeEvent, useState } from 'react'
import { isNil } from 'lodash'
import { yupResolver } from '@hookform/resolvers/yup'
import { useFieldArray, useForm } from 'react-hook-form'
import { CreateGeneratorElutionDto, GeneratorInstanceDto } from '@services/dtos'
import { generatorElutionSchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Alert, Button, Dialog, DialogContent, FormHelperText, Grid, IconButton, InputAdornment, Skeleton, TextField, Tooltip, Typography } from '@mui/material'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormHeader } from './FormHeader'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import InputField from '../inputs/InputField'
import { WrapperFormContent } from './WrapperFormContent'
import SyncIcon from '@mui/icons-material/Sync'
import { GeneratorInstanceCard } from '../generatorInstance/GeneratorInstanceCard'
import { MemberDto, MemberService } from '@services/api'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { MemberRoleEnumLabels } from '@utils/constants'
import { createGeneratorElution } from '@state/reducers/generatorElutionReducer'
import { useCurrentWorkbench } from '@hooks/useCurrentWorkbench'
import WorkbenchGeneratorInstanceForm from './WorkbenchGeneratorInstanceForm'
import { UpTransition } from '../animations'
import WorkbenchForm from './WorkbenchForm'
import { getWorkbenches } from '@state/reducers/workbenchReducer'
import DoseCalibratorStatus from '../DoseCalibratorStatus'

interface VolumeInputProps {
  value: number
  onChange: (value: number) => void
  errorMessage?: string
}

export function VolumeInput({ value, onChange, errorMessage }: VolumeInputProps) {
  const [inputValue, setInputValue] = useState(String(value))

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    setInputValue(raw)
    const parsed = parseFloat(raw)
    onChange(parsed)
  }

  const handleStep = (delta: number) => {
    const newValue = Math.max(0, value + delta)
    setInputValue(String(newValue))
    onChange(newValue)
  }

  return (
    <TextField
      label="Volume (mL)"
      value={inputValue}
      onChange={handleChange}
      size="small"
      type='number'
      onKeyDown={(e) => {
        if (e.key === '-') {
          e.preventDefault()
        }
      }}
      sx={{
        '& .MuiInputBase-root': { height: 56 },
        '& .MuiInputBase-input': {
          width: 46,
          textAlign: 'center',
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
          },
          MozAppearance: 'textfield',
        }
      }}
      error={!!errorMessage}
      helperText={errorMessage}
      inputProps={{ inputMode: 'decimal', width: 40 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconButton size="small" onClick={() => handleStep(-1)}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => handleStep(1)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  )
}

type Props = {
  onClose: () => void
}

const GeneratorElutionForm: React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const member = user && 'role' in user ? user as unknown as MemberDto : null
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [openGeneratorInstanceConfig, setOpenGeneratorInstanceConfig] = useState<boolean>(false)
  const [openWorkbenchForm, setOpenWorkbenchForm] = useState(false)

  const { handleSubmit, setValue, formState: { errors }, watch, control } = useForm<CreateGeneratorElutionDto>({
    resolver: yupResolver(generatorElutionSchema),
    defaultValues: {
      generatorInstances: [],
      performedById: member?.id,
    }
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'generatorInstances',
  })

  const { currentWorkbench, isLoading: isLoadingCurrentWorkbench } = useCurrentWorkbench()
  const generatorOccupiedSlots = currentWorkbench?.generatorSlots.filter(
    (slot): slot is typeof slot & { generatorInstance: GeneratorInstanceDto } =>
      !!slot.generatorInstance
  ) || []

  const generatorInstances = watch('generatorInstances')
  const selectedInstanceIds = generatorInstances.map(gi => gi.generatorInstanceId)

  const toggleGeneratorSelection = (generatorInstance: GeneratorInstanceDto) => {
    const existingIndex = selectedInstanceIds.indexOf(generatorInstance.id)
    if (existingIndex >= 0) {
      remove(existingIndex)
    } else {
      append({
        generatorInstanceId: generatorInstance.id,
        volumeMl: 5,
        activityAtRefMbq: undefined,
        activityRefAt: new Date(),
      })
    }
  }

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  const onSubmitForm = async (data: CreateGeneratorElutionDto) => {
    setIsSubmitting(true)
    dispatch(createGeneratorElution(data))
      .unwrap()
      .then(() => {
        cb('L\'élution a été créée avec succès')
        onClose()
      })
      .catch(() => cb('Une erreur est survenue. Vérifiez que l\'activité ne soit pas trop élevée comparée au générateur.'))
      .finally(() => setIsSubmitting(false))
  }

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full h-full flex flex-col"
      >
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title={'Nouvelle élution'}
          subTitles={[`Depuis ${currentWorkbench?.name ?? ''}`]}
          submitText={'Créer'}
        />

        <Grid container rowSpacing={2}>
          <Grid item xs={12}>
            <DoseCalibratorStatus />
          </Grid>

          <Grid item xs={12}>
            {(errors.generatorInstances as any)?.message && (
              <FormHelperText error>{(errors.generatorInstances as any).message}</FormHelperText>
            )}
            {isLoadingCurrentWorkbench ? (
              <div className="flex gap-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" width={495} height={90} />
                ))}
              </div>
            ) : generatorOccupiedSlots.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" className="!mb-1">
                  Sélection générateur(s) <Typography component="span" variant="body2" color="text.disabled">(au moins 1)</Typography>
                </Typography>
                <div className="flex gap-3 overflow-x-auto pb-2 [&::-webkit-scrollbar-thumb]:bg-gray-300">
                  {generatorOccupiedSlots.map(slot => (
                    <div key={slot.id} className="flex-shrink-0">
                      <GeneratorInstanceCard
                        generatorInstance={{ ...slot.generatorInstance, slotName: slot.name }}
                        selected={selectedInstanceIds.includes(slot.generatorInstance?.id)}
                        onSelect={toggleGeneratorSelection}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Alert severity="info" className="mb-4 mt-2">
                <Typography className="!mb-1" variant="body2">
                  {currentWorkbench?.generatorSlots.length === 0
                    ? 'Aucun emplacement de générateur sur l\'enceinte blindée.'
                    : 'Aucun générateur attribué aux emplacements de l\'enceinte blindée.'}
                </Typography>
                <Button variant="outlined" size="small" onClick={() => {
                  currentWorkbench?.generatorSlots.length === 0
                    ? setOpenWorkbenchForm(true)
                    : setOpenGeneratorInstanceConfig(true)
                }}>
                  {currentWorkbench?.generatorSlots.length === 0
                    ? `Modifier ${currentWorkbench?.name ?? ''}`
                    : `Modifier les générateurs de ${currentWorkbench?.name ?? ''}`}
                </Button>
              </Alert>
            )}
          </Grid>

          {fields.map((field, index) => {
            const slot = generatorOccupiedSlots.find(s => s.generatorInstance?.id === field.generatorInstanceId)
            const generatorInstance = slot?.generatorInstance
            const instanceErrors = errors.generatorInstances?.[index]
            const activityAtRefMbq = watch(`generatorInstances.${index}.activityAtRefMbq`)
            const volumeMl = watch(`generatorInstances.${index}.volumeMl`)
            const theoreticalActivity = generatorInstance?.theoreticalActivityAtTimeMbq

            return (
              <Grid item xs={12} key={field.id}>
                <WrapperFormContent legend={`Générateur ${generatorInstance?.serialNumber ?? index + 1}`}>
                  <div className="flex flex-wrap gap-4 justify-center items-center w-full">
                    <InputField
                      control={control}
                      name={`generatorInstances.${index}.activityAtRefMbq`}
                      label={'Activité (MBq)'}
                      type={'number'}
                      inputProps={{ min: 0, step: '0.01' }}
                      sx={{ width: 140 }}
                    />

                    <VolumeInput
                      value={watch(`generatorInstances.${index}.volumeMl`) ?? 5}
                      onChange={(val) => setValue(`generatorInstances.${index}.volumeMl`, val)}
                      errorMessage={instanceErrors?.volumeMl?.message}
                    />

                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <TimePicker
                        label="Heure mesure"
                        disableFuture
                        value={watch(`generatorInstances.${index}.activityRefAt`) ? moment(watch(`generatorInstances.${index}.activityRefAt`)) : null}
                        onChange={(newValue) => {
                          if (!newValue?.isValid()) {
                            setValue(`generatorInstances.${index}.activityRefAt`, undefined)
                            return
                          }
                          const ref = moment().set({
                            hour: newValue.hour(),
                            minute: newValue.minute(),
                            second: 0,
                            millisecond: 0,
                          })
                          setValue(`generatorInstances.${index}.activityRefAt`, ref.toDate())
                        }}
                        ampm={false}
                        views={['hours', 'minutes']}
                        timeSteps={{ minutes: 1 }}
                        slotProps={{
                          textField: {
                            sx: { '& .MuiInputBase-input': { width: 52 } },
                            error: !!instanceErrors?.activityRefAt,
                            helperText: instanceErrors?.activityRefAt?.message,
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Tooltip title="Heure actuelle" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => setValue(`generatorInstances.${index}.activityRefAt`, new Date())}
                                    >
                                      <SyncIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>

                  {activityAtRefMbq !== undefined && activityAtRefMbq < 10 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Activité faible ({'<'} 10 MBq)
                    </Alert>
                  )}
                  {volumeMl !== undefined && (volumeMl <= 3 || volumeMl >= 15) && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Volume inhabituel ({volumeMl <= 3 ? '≤ 3 mL' : '≥ 15 mL'})
                    </Alert>
                  )}
                  {!isNil(activityAtRefMbq) && !isNil(theoreticalActivity) && activityAtRefMbq > theoreticalActivity && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Activité dépasse l{'\''}activité théorique du générateur
                    </Alert>
                  )}
                </WrapperFormContent>
              </Grid>
            )
          })}

          <Grid item xs={12}>
            <WrapperFormContent legend={'Informations générales'}>
              <div className="flex flex-wrap gap-4 justify-center items-center w-full">
                <ServerAutocomplete<MemberDto>
                  width={220}
                  defaultValue={member}
                  initialOptions={member ? [member] : []}
                  value={member ?? null}
                  fetchService={fetchMembers}
                  onChange={(value: MemberDto | null) => {
                    setValue('performedById', value?.id)
                  }}
                  getValue={(value) => value?.id?.toString() || null}
                  getOptionLabel={(value) => {
                    const member = value as MemberDto
                    if (!member) {
                      return ''
                    }
                    const roleLabel = MemberRoleEnumLabels[member.role]
                    return `${member.firstName} ${member.lastName}${roleLabel ? ` (${roleLabel})` : ''}`
                  }}
                  debounceMs={300}
                  placeholder="Rechercher un membre..."
                  label="Agent"
                  helperText={errors.performedById?.message}
                  error={!!errors.performedById}
                  minSearchLength={0}
                />
              </div>

              <div className="w-full mt-4">
                <InputField
                  control={control}
                  name={'notes'}
                  label={'Observation'}
                  type={'text'}
                  multiline
                  rows={2}
                  sx={{
                    '& .MuiInputBase-input': {
                      padding: '4px !important',
                      fontSize: '0.875rem',
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'black',
                      opacity: 1,
                    },
                  }}
                />
              </div>
            </WrapperFormContent>
          </Grid>
        </Grid>
      </form>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={openGeneratorInstanceConfig}
        TransitionComponent={UpTransition}
        onClose={() => setOpenGeneratorInstanceConfig(false)}>
        <DialogContent>
          {currentWorkbench && (
            <WorkbenchGeneratorInstanceForm
              workbench={currentWorkbench}
              onClose={() => setOpenGeneratorInstanceConfig(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={openWorkbenchForm}
        maxWidth="md"
        fullWidth
        onClose={() => setOpenWorkbenchForm(false)}>
        {currentWorkbench && <WorkbenchForm
          editingWorkbench={currentWorkbench}
          onClose={() => {
            dispatch(getWorkbenches())
            setOpenWorkbenchForm(false)
          }}
        />}
      </Dialog>
    </div>
  )
}

export default GeneratorElutionForm
